// Writable columns per table — the whitelist used by the API (SQL) and by the web outbox (bodies).

export const LEAD_FIELDS = [
  'business_name',
  'business_type',
  'address',
  'address_city',
  'address_postal_code',
  'place_id',
  'neighborhood',
  'owner_name',
  'owner_role',
  'owner_language',
  'phone',
  'email',
  'instagram',
  'current_pos',
  'pos_monthly_cost',
  'contract_end_date',
  'etf_amount',
  'stage',
  'heat',
  'best_visit_window',
  'notes',
  // the conversion funnel's milestones (D92) — Artur's own log, never Square's
  'meeting_on',
  'demo_on',
  'application_on',
  'activation_on',
  'gpv_usd',
  'gpv_as_of',
] as const;
export type LeadField = (typeof LEAD_FIELDS)[number];

export const VISIT_FIELDS = [
  'lead_id',
  'visited_at',
  'spoke_with',
  'outcome',
  'notes',
  'next_action',
  'next_action_date',
  'next_action_time',
  // the touch log (D90)
  'channel',
  'persona',
  'variant',
] as const;
export type VisitField = (typeof VISIT_FIELDS)[number];

export const OBJECTION_FIELDS = ['lead_id', 'visit_id', 'type', 'detail'] as const;
export type ObjectionField = (typeof OBJECTION_FIELDS)[number];

/** The OC Health plan-check fields the "Send to CRM" bookmark sends (D58) — nothing else leaves the page. */
export const PLAN_CHECK_FIELDS = [
  'PRNumber',
  'planreviewsID',
  'FacilityName',
  'FacilityOwner',
  'FacilityAddress',
  'facilityCity',
  'facilityZip',
  'PlanReviewType',
  'foodSubtype',
  'ScopeofWorkExtentofRemodel',
  'PlanReviewSubmissionDate',
  'RecordStatus',
  'StatusofPlans',
  'Contact_BillinglastName',
  'ProjectOwnerEmail',
  'ProjectOwnerPhone',
] as const;
export type PlanCheckRecord = Partial<Record<(typeof PLAN_CHECK_FIELDS)[number], string | null>>;

/** Where OC Health plan checks are read (D58): the vendor portal and its plan-review search page. */
export const OCHCA_PORTAL_ORIGIN = 'https://inspections.myhealthdepartment.com';
export const OCHCA_PLAN_REVIEW_URL = `${OCHCA_PORTAL_ORIGIN}/orange-county/plan-review`;

/** The City of Anaheim's business-license open data (D60): no page per license, so its list. */
export const ANAHEIM_LICENSES_URL =
  'https://data-anaheim.opendata.arcgis.com/datasets/bc7cad50bfbc4ba4bb6b3c87336cad4e_0';

/** The cities whose planning items are on Legistar's public API, by its name for each (D64; Fullerton's has only its council). */
export const AGENDA_CITIES = {
  newportbeach: 'Newport Beach',
  huntingtonbeach: 'Huntington Beach',
  costamesa: 'Costa Mesa',
  cityoforange: 'Orange',
} as const;
export type AgendaCity = keyof typeof AGENDA_CITIES;
/** Anaheim's land-use permit requests (D64): no page per request, so its signals link the dataset. */
export const ANAHEIM_PERMITS_URL =
  'https://data-anaheim.opendata.arcgis.com/datasets/41a5b221867f4784b29c5260ab87c238';

/** Monthly city new-business lists (D61): Huntington Beach replaces one PDF; Yorba Linda and La Habra
 *  list each month's file on a page. Each signal links here: the lists have no page per business. */
export const CITY_LIST_URLS = {
  huntington_beach: 'https://www3.huntingtonbeachca.gov/files/finance/new-business-licenses.pdf',
  yorba_linda: 'https://www.yorbalindaca.gov/748/New-Business-Lists',
  la_habra: 'https://www.lahabraca.gov/1303/Business-License-Listing',
  costa_mesa:
    'https://www.costamesaca.gov/government/departments-and-divisions/finance/business-licenses/inquire-about-business-license',
  laguna_beach:
    'https://www.lagunabeachcity.net/government/departments/administrative-services/finance-division/business-licenses',
  irvine: 'https://secure.cityofirvine.org/webreport/',
  newport_beach: 'https://apps.newportbeachca.gov/revenue/',
  lake_forest: 'https://www.lakeforestca.gov/departments/economic_development/index.php',
  fullerton: 'https://buslic.cityoffullerton.com/PrimeWeb/Search/Index/BusinessLicense',
  orange: 'https://orange.hdlgov.com/Search/Index/BusinessLicense',
  tustin: 'https://tustin.hdlgov.com/Search/Index/BusinessLicense',
  westminster: 'https://westminster.hdlgov.com/Search/Index/BusinessLicense',
  brea: 'https://brea.hdlgov.com/Search/Index/BusinessLicense',
  cypress: 'https://cypress.hdlgov.com/Search/Index/BusinessLicense',
  fountain_valley: 'https://fountainvalley.hdlgov.com/Search/Index/BusinessLicense',
  stanton: 'https://stanton.hdlgov.com/Search/Index/BusinessLicense',
  seal_beach: 'https://sealbeach.hdlgov.com/Search/Index/BusinessLicense',
  san_clemente: 'https://sanclemente.hdlgov.com/Search/Index/BusinessLicense',
  los_alamitos: 'https://losalamitos.hdlgov.com/Search/Index/BusinessLicense',
} as const;

/** Read by the server every morning (D61). */
export const AUTO_CITIES = ['huntington_beach', 'yorba_linda', 'la_habra', 'lake_forest'] as const;
/** Newport Beach's full license roll (D77): one CSV, refreshed monthly, with start dates and phones. */
export const NEWPORT_BEACH_CSV_URL = 'https://apps.newportbeachca.gov/revenue/download/nbbusiness.csv';
/** Their sites refuse scripts: the rep downloads the monthly list and uploads it (D61, owner decision). */
export const UPLOAD_CITIES = ['costa_mesa', 'laguna_beach'] as const;
/** Where a downloaded list can be mailed to import itself (D78). */
export const INTAKE_ADDRESS = 'leads@crm.cayreslabs.com';
/** HdL license searches behind Cloudflare: read by the rep's browser with the Send to CRM bookmark, from
 *  each city's own HdL page (D62, owner decision). Laguna Beach's comes in this way too, or by upload. */
export const HDL_BROWSER_HOSTS = {
  westminster: 'https://westminster.hdlgov.com',
  brea: 'https://brea.hdlgov.com',
  cypress: 'https://cypress.hdlgov.com',
  fountain_valley: 'https://fountainvalley.hdlgov.com',
  stanton: 'https://stanton.hdlgov.com',
  seal_beach: 'https://sealbeach.hdlgov.com',
  san_clemente: 'https://sanclemente.hdlgov.com',
  los_alamitos: 'https://losalamitos.hdlgov.com',
  laguna_beach: 'https://lagunabeach.hdlgov.com',
} as const;

/** Sources whose records carry no phone, so a signal's phone is Google's and its card says so (D59–D61);
 *  OC Health, Yorba Linda and La Habra give their own and Google is never asked for them. */
export const GOOGLE_PHONE_SOURCES = ['abc', 'anaheim', 'huntington_beach', 'fbn'] as const;

/** The OC Clerk-Recorder's business-name search (D63): no page per filing, so the search. */
export const FBN_SEARCH_URL = 'https://cr.occlerkrecorder.gov/FBNInternet/FBNSearch/Default.aspx';
/** The county's own site, read by the rep's browser with the Send to CRM bookmark (D76): its server never
 *  answers Cloudflare's network, so the server only judges what the bookmark hands over. */
export const FBN_ORIGIN = 'https://cr.occlerkrecorder.gov';
/** The bookmark searches this many filing days back, and hands over at most this many rows and details. */
export const FBN_LOOKBACK_DAYS = 7;
export const FBN_MAX_ROWS = 3000;
export const FBN_MAX_DETAILS = 150;
/** A filing as the results page lists it: the file number, the business name, the filing day, and what the
 *  page posts to show the filing's details. */
export interface FbnRowRecord {
  file: string;
  name: string;
  filedOn: string;
  detail: string;
}
/** A filing's page, read by the bookmark: who registered it, and the statements it lists (its own, and
 *  any earlier one behind it — a renewal). */
export interface FbnDetailRecord {
  file: string;
  registrants: string[];
  statements: string[];
}

/** The official page of one ABC license. */
export const abcLicenseUrl = (license: string): string =>
  `https://www.abc.ca.gov/licensing/license-lookup/single-license/?RPTTYPE=12&LICENSE=${encodeURIComponent(license)}`;
